import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technician: any;
}

export default function EditTechnicianDialog({ open, onOpenChange, technician }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(technician?.name || "");
  const [phone, setPhone] = React.useState(technician?.phone || "");
  const [email, setEmail] = React.useState(technician?.email || "");
  const [specializations, setSpecializations] = React.useState(
    Array.isArray(technician?.specializations) ? technician.specializations.join(", ") : ""
  );

  React.useEffect(() => {
    setName(technician?.name || "");
    setPhone(technician?.phone || "");
    setEmail(technician?.email || "");
    setSpecializations(Array.isArray(technician?.specializations) ? technician.specializations.join(", ") : "");
  }, [technician]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("technicians")
        .update({
          name,
          phone,
          email,
          specializations: specializations.split(",").map((s) => s.trim()).filter(Boolean),
          updated_at: new Date().toISOString(),
        })
        .eq("id", technician.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["technician", technician.id] });
      queryClient.invalidateQueries({ queryKey: ["technicians-list"] });
      toast.success("Technician updated!");
      onOpenChange(false);
    },
    onError: () => toast.error("Update failed."),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Technician</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="specs">Specializations (comma-separated)</Label>
            <Input id="specs" value={specializations} onChange={(e) => setSpecializations(e.target.value)} placeholder="e.g. splicing, testing" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
